import ImageNotSupportedOutlined from "@mui/icons-material/ImageNotSupportedOutlined";
import { contentReviewService } from "../../../services/contentReviewService";
import React from "react";
import { Box, Button, Chip, Stack, Typography, Tooltip } from "@mui/material";
import SocialsCard from "../SocialsCard";
import { displayCount, type SocialsCount } from "../../../lib/socialsCounts";
import { humaniseTemplate, isTypeRecorded } from "../../../lib/socialsFilters";
import type { SchedulableReel } from "../../../lib/postingCalendar";

/**
 * Ch94 · NEEDS YOU TODAY — the point of the Overview page.
 *
 * 🔴 WHAT WAS WRONG BEFORE, in Grant's words on seeing it: "the mockup you gave
 * me didnt look complete". The tiles showed a NUMBER and a text link. The mock
 * he approved puts the actual work IN the tile — the next three reels with
 * their thumbnails, names, type and age; the approved ones each with their own
 * Schedule button; the top conversations with their HOT tags — so what needs
 * him is one click away rather than one navigation away.
 *
 * A number is a summary of work. These tiles are the work.
 */

/** A reel row as the mock draws it: swatch, name, type chip, age. */
export interface TileReel {
  id: string;
  name: string;
  template: string;
  frameSheetAvailable?: boolean;
  /** "2h", "1d" — already humanised, or null when we genuinely cannot say. */
  age: string | null;
  /** Waiting long enough that the mock colours it. */
  stale?: boolean;
  /** A per-row action, e.g. Schedule. */
  action?: React.ReactNode;
}

export interface TileThread {
  id: string;
  title: string;
  source: string;
  hot: boolean;
}

/** Use the same signed frame sheet and top-left crop as the Reels grid. */
const ReelPreview: React.FC<{ reel: TileReel }> = ({ reel }) => {
  const [media, setMedia] = React.useState<{id: string; url: string | null; failed: boolean} | null>(null);
  React.useEffect(() => {
    let current = true;
    setMedia(null);
    if (reel.frameSheetAvailable) {
      contentReviewService.media(reel.id, "frame-sheet")
        .then(link => { if (current) setMedia({id: reel.id, url: link.url, failed: false}); })
        .catch(() => { if (current) setMedia({id: reel.id, url: null, failed: true}); });
    }
    return () => { current = false; };
  }, [reel.id, reel.frameSheetAvailable]);
  const preview = media?.id === reel.id ? media : null;
  const label = preview?.failed ? "The preview could not be loaded" : reel.frameSheetAvailable ? "Loading preview…" : "Preview not available";
  return <Box sx={{width: 40, height: 64, borderRadius: 1, flexShrink: 0, overflow: "hidden", position: "relative", bgcolor: "action.hover"}}>
    {preview?.url && !preview.failed ? <Box component="img" alt={`${reel.name} · first frame`} src={preview.url}
      onError={() => setMedia({id: reel.id, url: null, failed: true})}
      sx={{position: "absolute", width: "200%", height: "200%", maxWidth: "none", top: 0, left: 0}} /> :
      <Tooltip title={label}><Box role="img" aria-label={label} sx={{height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary"}}><ImageNotSupportedOutlined sx={{fontSize: 20}} /></Box></Tooltip>}
  </Box>;
};

const ReelRow: React.FC<{ reel: TileReel }> = ({ reel }) => (
  <Stack
    direction="row"
    alignItems="center"
    gap={1}
    sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}
    data-testid="tile-reel-row"
  >
    <ReelPreview reel={reel} />
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
        {reel.name}
      </Typography>
      {isTypeRecorded(reel.template) ? (
        <Chip
          size="small"
          label={humaniseTemplate(reel.template)}
          sx={{ height: 18, fontSize: 12, mt: 0.25 }}
        />
      ) : null}
    </Box>
    {reel.action}
    {/* An age we cannot read renders nothing — never a guessed "just now". */}
    {reel.age ? (
      <Typography
        variant="caption"
        // The mock colours an age only when it has been waiting too long;
        // everything else stays quiet, or every row shouts.
        color={reel.stale ? "error.main" : "text.secondary"}
        sx={{ flexShrink: 0 }}
        data-testid="tile-age"
      >
        {reel.age}
      </Typography>
    ) : null}
  </Stack>
);

interface Props {
  schedulingState?: "live" | "to_build" | "not_connected";
  toReview: SocialsCount;
  reviewQueue: TileReel[];
  onStartReviewing?: () => void;

  approvedWaiting: SocialsCount;
  readyToSchedule: SchedulableReel[];
  onScheduleOne?: (reviewId: string) => void;

  conversations: SocialsCount;
  threads: TileThread[];
  onOpenEngage?: () => void;
  onOpenThread?: (id: string) => void;
  /** Instagram's honest state, shown inside the conversations tile as in the mock. */
  instagramNote?: string;
}

const Big: React.FC<{ count: SocialsCount }> = ({ count }) => {
  const shown = displayCount(count);
  return (
    <Typography variant="h3" sx={{ lineHeight: 1 }} title={shown.title ?? undefined}>
      {shown.text}
    </Typography>
  );
};

const NeedsYouToday: React.FC<Props> = ({
  toReview,
  reviewQueue,
  onStartReviewing,
  approvedWaiting,
  schedulingState = "to_build",
  readyToSchedule,
  onScheduleOne,
  conversations,
  threads,
  onOpenEngage, onOpenThread,
  instagramNote,
}) => (
  <Box>
    <Typography variant="overline" color="text.secondary">
      Needs you today
    </Typography>
    <Box
      sx={{
        mt: 1,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
        gap: 2,
        alignItems: "stretch",
      }}
      data-testid="needs-you-today"
    >
      <SocialsCard title="Reels to review" state="live" testId="needs-you-tile">
        <Big count={toReview} />
        <Box sx={{ mt: 1 }}>
          {reviewQueue.slice(0, 3).map((reel) => (
            <ReelRow key={reel.id} reel={reel} />
          ))}
        </Box>
        <Button
          fullWidth
          variant="contained"
          sx={{ mt: 2 }}
          onClick={onStartReviewing}
          disabled={!onStartReviewing || reviewQueue.length === 0}
          data-testid="start-reviewing"
        >
          Start reviewing
        </Button>
      </SocialsCard>

      <SocialsCard
        title="Approved, waiting for a slot"
        state={schedulingState}
        testId="needs-you-tile"
      >
        <Big count={approvedWaiting} />
        <Box sx={{ mt: 1 }}>
          {readyToSchedule.slice(0, 3).map((reel) => (
            <ReelRow
              key={reel.reviewId}
              reel={{
                id: reel.reviewId,
                name: reel.subject,
                template: reel.template,
                age: null,
                action: onScheduleOne ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => onScheduleOne(reel.reviewId)}
                    data-testid="tile-schedule"
                  >
                    Schedule
                  </Button>
                ) : undefined,
              }}
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
          {readyToSchedule.length === 0
            ? "Nothing is approved and waiting. Approve a reel and it shows up here."
            : "Or drag a reel onto the calendar below."}
        </Typography>
      </SocialsCard>

      <SocialsCard title="Conversations to join" state="live" testId="needs-you-tile">
        <Stack direction="row" alignItems="baseline" gap={1}>
          <Big count={conversations} />
          <Typography variant="caption" color="text.secondary">
            to reply to
          </Typography>
        </Stack>
        <Box sx={{ mt: 1 }}>
          {threads.slice(0, 2).map((thread) => (
            <Stack
              key={thread.id}
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}
              data-testid="tile-thread-row"
              role="button" tabIndex={0} onClick={() => onOpenThread ? onOpenThread(thread.id) : onOpenEngage?.()}
              onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); if (onOpenThread) onOpenThread(thread.id); else onOpenEngage?.(); } }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {thread.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {thread.source}
                </Typography>
              </Box>
              {thread.hot ? (
                <Chip size="small" color="warning" label="HOT" sx={{ height: 18, fontSize: 12 }} />
              ) : null}
            </Stack>
          ))}
        </Box>
        {instagramNote ? (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {instagramNote}
          </Typography>
        ) : null}
        <Button
          fullWidth
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={onOpenEngage}
          disabled={!onOpenEngage}
          data-testid="open-engage"
        >
          Open Engage
        </Button>
      </SocialsCard>
    </Box>
  </Box>
);

export default NeedsYouToday;
